/**
 * O painel da conversa — o DOM, montado UMA vez.
 *
 * Compartilhado entre a página da mesa (`game/netmain.ts`) e a tela "minha
 * mesa" do lobby (`ui/app.ts`): a conversa começa na sala e continua na
 * partida, e as duas telas mostram a mesma fila. A lógica pura (fila, rótulo
 * de quem falou, o que pode ir) mora em `game/chat.ts`, com teste; aqui é só
 * o desenho e os cliques.
 *
 * Fora de qualquer `innerHTML` de propósito: as duas telas redesenham o
 * resto a cada estado, e um campo de texto redesenhado perde o foco e o que
 * o jogador estava digitando. Quem monta guarda `el` e o move para onde ele
 * deve aparecer — mover um nó preserva o que está dentro.
 */
import { CHAT_MAX_CHARS } from '@sol-pool/protocol'
import { localeDeFormatacao, t } from '@/i18n'
import { agruparMensagens, podeEnviar, type MensagemDeChat } from '@/game/chat'
import { esc } from '@/ui/esc'

/** "14:05", no relógio de quem lê. */
export const horaDaMensagem = (at: number): string =>
  new Intl.DateTimeFormat(localeDeFormatacao(), { hour: '2-digit', minute: '2-digit' }).format(at)

/**
 * A lista em BALÕES: os meus à direita, os dos outros à esquerda, e a mesma
 * pessoa falando em sequência com o nome uma vez só (`agruparMensagens`).
 *
 * A hora vai no cabeçalho do grupo, e a de cada balão no `title`: numa coluna
 * estreita, uma hora por linha comeria o espaço do texto.
 *
 * TUDO escapado: o texto e o apelido vieram de outra pessoa pela rede.
 */
export function htmlDaListaDoChat(
  mensagens: readonly MensagemDeChat[],
  eu: 0 | 1 | null,
  hora: (at: number) => string = horaDaMensagem,
): string {
  if (mensagens.length === 0) return `<li class="chat-vazio">${esc(t('chat.vazio'))}</li>`
  return agruparMensagens(mensagens, eu)
    .map(
      (g) => `<li class="chat-grupo" data-tom="${g.tom}">
        <span class="chat-cabeca"><span class="chat-autor">${esc(g.autor)}</span>
          <time class="chat-hora">${esc(hora(g.mensagens[0]!.at))}</time></span>
        ${g.mensagens.map((m) => `<p class="chat-bolha" title="${esc(hora(m.at))}">${esc(m.text)}</p>`).join('')}
      </li>`,
    )
    .join('')
}

/**
 * As frases rápidas: um toque e a frase vai, sem abrir o teclado.
 *
 * No celular, digitar no meio da partida é tirar o dedo da mesa e o teclado
 * cobrir metade da tela — e o que mais se diz numa mesa é sempre o mesmo
 * punhado de coisas. A frase sai pelo mesmo caminho do campo de texto (o
 * `enviar` de quem monta o chat), então passa pelos mesmos limites do servidor.
 *
 * Três ficam SEMPRE à vista, as mais ditas numa mesa. Era uma fileira com
 * todas, rolando de lado — a última saía cortada ("Val…") e nada dizia que dava
 * para arrastar. O "+" abre o resto em abas, para a grade não passar da
 * altura do celular deitado.
 */
export const FRASES_A_VISTA = ['chat.rapida.boaSorte', 'chat.rapida.boaJogada', 'chat.rapida.gg'] as const

/**
 * O resto, por aba — uma para cada momento da partida: chegar e se despedir,
 * elogiar, provocar e dar um recado. No máximo seis por aba (três linhas de
 * duas): mais que isso a grade passa da altura do celular deitado.
 *
 * Provocação só LEVE ("Hoje não!", "Sorte de principiante"): o bastante para
 * animar a mesa sem azedar o clima numa partida valendo SOL. "Muito fraco!" e
 * "Muito forte!" servem dos dois jeitos — zoar ou comentar a força da tacada.
 */
export const ABAS_DE_FRASES = {
  saudar: ['chat.rapida.oi', 'chat.rapida.bomJogo', 'chat.rapida.revanche', 'chat.rapida.ateAProxima', 'chat.rapida.tchau'],
  elogiar: ['chat.rapida.queTacada', 'chat.rapida.mandouBem', 'chat.rapida.quase', 'chat.rapida.jogouMuito', 'chat.rapida.queAula'],
  provocar: [
    'chat.rapida.hojeNao',
    'chat.rapida.sorteDePrincipiante',
    'chat.rapida.muitoFraco',
    'chat.rapida.muitoForte',
    'chat.rapida.taNervoso',
    'chat.rapida.pressao',
  ],
  avisos: ['chat.rapida.suaVez', 'chat.rapida.pensando', 'chat.rapida.jaVolto', 'chat.rapida.foiMal', 'chat.rapida.valeu'],
} as const

export type AbaDeFrases = keyof typeof ABAS_DE_FRASES

/** Todas, sem repetir: as à vista e as das abas. */
export const FRASES_RAPIDAS = [...FRASES_A_VISTA, ...Object.values(ABAS_DE_FRASES).flat()]

/** As frases rápidas: as três à vista, o "+" e as abas com o resto (fechadas). */
export function htmlDasFrasesRapidas(ativo: boolean): string {
  const desligado = ativo ? '' : 'disabled'
  const botao = (chave: (typeof FRASES_RAPIDAS)[number]): string =>
    `<button type="button" class="chat-rapida" data-frase="${esc(t(chave))}" ${desligado}>${esc(t(chave))}</button>`
  const abas = Object.keys(ABAS_DE_FRASES) as AbaDeFrases[]
  return `<div class="chat-rapidas" role="group" aria-label="${esc(t('chat.rapidas'))}">
      ${FRASES_A_VISTA.map(botao).join('')}
      <button type="button" class="chat-rapida chat-rapida-mais" data-frases-mais aria-expanded="false"
              aria-controls="chat-frases-grade" aria-label="${esc(t('chat.maisFrases'))}" title="${esc(t('chat.maisFrases'))}"
              ${desligado}>+</button>
    </div>
    <div class="chat-frases-grade" id="chat-frases-grade" hidden>
      <div class="chat-frases-abas" role="tablist" aria-label="${esc(t('chat.rapidas'))}">
        ${abas
          .map(
            (aba, i) => `<button type="button" class="chat-frases-aba" role="tab" data-aba-frases="${aba}"
              aria-selected="${i === 0}" ${desligado}>${esc(t(`chat.aba.${aba}`))}</button>`,
          )
          .join('')}
      </div>
      ${abas
        .map(
          (aba, i) => `<div class="chat-frases-lista" role="tabpanel" data-painel-frases="${aba}" ${i === 0 ? '' : 'hidden'}>
          ${ABAS_DE_FRASES[aba].map(botao).join('')}
        </div>`,
        )
        .join('')}
    </div>`
}

export type PainelDeChat = {
  el: HTMLDetailsElement
  /** Redesenha a lista se ela mudou. `eu` é o índice de quem lê, ou nulo para a plateia. */
  desenhar(mensagens: readonly MensagemDeChat[], eu: 0 | 1 | null): void
  /** Uma recusa do servidor, ao lado do campo, por alguns segundos. */
  erro(mensagem: string): void
}

export function montarChat(opts: {
  /** Manda a frase; devolve se ela saiu. Nulo: sem servidor (demo), campo desligado. */
  enviar: ((texto: string) => boolean) | null
  placeholder?: string
  /** Abre já expandido — na tela da sala, a conversa é o assunto. */
  aberto?: boolean
  /** Sem as margens da página da mesa: embutido num painel. */
  embutido?: boolean
}): PainelDeChat {
  const ativo = opts.enviar !== null
  const el = document.createElement('details')
  el.className = `chat${opts.embutido ? ' chat-embutido' : ''}`
  el.open = opts.aberto ?? false
  el.innerHTML = `
    <summary class="chat-titulo">${t('chat.titulo')} <span class="chat-n" hidden></span></summary>
    <ul class="chat-lista" aria-live="polite"></ul>
    <p class="chat-erro" hidden></p>
    ${htmlDasFrasesRapidas(ativo)}
    <form class="chat-form">
      <input class="chat-texto" maxlength="${CHAT_MAX_CHARS}" autocomplete="off"
             placeholder="${esc(opts.placeholder ?? (ativo ? t('chat.placeholder') : t('chat.placeholderDemo')))}"
             ${ativo ? '' : 'disabled'} />
      <button type="submit" class="chat-enviar" ${ativo ? '' : 'disabled'}>${t('chat.enviar')}</button>
    </form>`

  const lista = el.querySelector('.chat-lista') as HTMLUListElement
  const selo = el.querySelector('.chat-n') as HTMLElement
  const erroEl = el.querySelector('.chat-erro') as HTMLElement
  const campo = el.querySelector('.chat-texto') as HTMLInputElement
  const form = el.querySelector('.chat-form') as HTMLFormElement

  /** Quantas já foram desenhadas; evita reescrever a lista à toa. */
  let desenhadas = -1
  /** Quantas foram vistas com o painel aberto; o resto conta como novas. */
  let lidas = 0
  let total = 0
  let erroTimer: number | null = null

  const desenhar = (mensagens: readonly MensagemDeChat[], eu: 0 | 1 | null): void => {
    total = mensagens.length
    if (mensagens.length === desenhadas) return
    desenhadas = mensagens.length

    lista.innerHTML = htmlDaListaDoChat(mensagens, eu)
    lista.scrollTop = lista.scrollHeight

    if (el.open) lidas = mensagens.length
    const novas = mensagens.length - lidas
    selo.hidden = novas <= 0
    selo.textContent = String(novas)
  }

  el.addEventListener('toggle', () => {
    if (!el.open) return
    lidas = total
    selo.hidden = true
    campo.focus()
  })

  // Enter envia: o `<form>` cuida disso sem tecla especial.
  form.addEventListener('submit', (e) => {
    e.preventDefault()
    if (!opts.enviar) return
    const texto = campo.value
    if (!podeEnviar(texto)) return
    if (opts.enviar(texto)) campo.value = ''
  })

  // O "+" abre e fecha a grade com todas as frases.
  const mais = el.querySelector<HTMLButtonElement>('[data-frases-mais]')
  const grade = el.querySelector<HTMLElement>('.chat-frases-grade')
  const abrirGrade = (aberta: boolean): void => {
    if (!mais || !grade) return
    grade.hidden = !aberta
    mais.setAttribute('aria-expanded', String(aberta))
    mais.textContent = aberta ? '×' : '+'
  }
  mais?.addEventListener('click', () => abrirGrade(grade?.hidden ?? false))

  // As abas (Elogios, Provocações): uma lista à vista por vez.
  const abas = el.querySelectorAll<HTMLButtonElement>('[data-aba-frases]')
  abas.forEach((aba) =>
    aba.addEventListener('click', () => {
      abas.forEach((a) => a.setAttribute('aria-selected', String(a === aba)))
      el.querySelectorAll<HTMLElement>('[data-painel-frases]').forEach((p) => {
        p.hidden = p.dataset.painelFrases !== aba.dataset.abaFrases
      })
    }),
  )

  // Uma frase rápida vai direto, pelo mesmo `enviar` do campo de texto — e a
  // grade fecha: escolhida a frase, ela já cumpriu o que veio fazer.
  el.querySelectorAll<HTMLButtonElement>('[data-frase]').forEach((b) =>
    b.addEventListener('click', () => {
      const frase = b.dataset.frase ?? ''
      if (opts.enviar && podeEnviar(frase)) opts.enviar(frase)
      abrirGrade(false)
    }),
  )

  const erro = (mensagem: string): void => {
    erroEl.textContent = mensagem
    erroEl.hidden = false
    if (erroTimer !== null) clearTimeout(erroTimer)
    erroTimer = window.setTimeout(() => {
      erroEl.hidden = true
    }, 4_000)
  }

  return { el, desenhar, erro }
}
